// Listing-favorites client shared by the page-wide Stimulus controller
// (connect_favorites_controller.js) and the SearchMapIsland's React cards
// (SUR-2374). Talks to /account/favorites on the Connect host: session-cookie
// authenticated (credentials: same-origin), no CSRF token — the server
// enforces an Origin/Referer same-origin check instead, like every other
// Connect endpoint. A 401 means "not signed in" and doubles as the sign-in
// probe (same contract as /account/saved_searches).

import { connectBase } from "../base_path";

// Key gains the /_connect/:slug base as a suffix ONLY in path-preview mode, so
// agents sharing the dev origin don't leak pending favorites across sites; on a
// real subdomain the key is unchanged.
export const PENDING_KEY = `ssc:pending-favorite${connectBase() ? `:${connectBase()}` : ""}`;

// -> { signedIn: true, keys: Set } | { signedIn: false, keys: Set } | null
// (null = network/server error: callers leave the hearts untouched).
export async function fetchFavorites() {
  try {
    const res = await fetch("/account/favorites", { credentials: "same-origin" });
    if (res.status === 401) return { signedIn: false, keys: new Set() };
    if (!res.ok) return null;
    const data = await res.json();
    return { signedIn: true, keys: new Set(Array.isArray(data.listing_keys) ? data.listing_keys : []) };
  } catch {
    return null;
  }
}

// `listing` is the optional display snapshot ({ address, price, mls_number,
// url, hero_photo }) the server stores for later favorite lists / timeline.
// -> "ok" | "unauthorized" | "error"
export async function addFavorite(key, listing = null) {
  try {
    const body = { listing_key: key };
    if (listing) body.listing = listing;
    const res = await fetch("/account/favorites", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      credentials: "same-origin",
    });
    if (res.status === 401) return "unauthorized";
    return res.ok ? "ok" : "error";
  } catch {
    return "error";
  }
}

export async function removeFavorite(key) {
  try {
    const res = await fetch(`/account/favorites/${encodeURIComponent(key)}`, {
      method: "DELETE",
      credentials: "same-origin",
    });
    if (res.status === 401) return "unauthorized";
    return res.ok ? "ok" : "error";
  } catch {
    return "error";
  }
}

// Signed-out → open the Stimulus-owned sign-in modal in the page header
// (the SUR-2174 passwordless flow). Same bridge SearchMapIsland already uses.
export function openSignIn() {
  document.querySelector('[data-connect-session-target="signInBtn"]')?.click();
}

// Pending-favorite handoff: an anonymous heart tap stashes the key, the
// sign-in flow reloads the same page (connect_registration_controller
// redirects back with ?registered=1), and the favorites controller consumes
// the stash on hydrate. sessionStorage: survives the reload, dies with the
// tab — a magic link finished elsewhere loses it (accepted; matches
// ssc:last-search semantics). try/catch for private-mode storage denial.
export function stashPendingFavorite(key, listing = null) {
  try {
    window.sessionStorage.setItem(PENDING_KEY, JSON.stringify({ key, listing }));
  } catch {
    /* storage unavailable — the tap just opens sign-in */
  }
}

// -> { key, listing } | null. Leaves the stash in place — the caller clears
// it with clearPendingFavorite() only once the replay POST actually
// persisted, so a network/server failure keeps the tap alive for a retry on
// the next page load.
export function peekPendingFavorite() {
  try {
    const raw = window.sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed.key === "string" && parsed.key ? parsed : null;
  } catch {
    return null;
  }
}

export function clearPendingFavorite() {
  try {
    window.sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}
